<script setup lang="ts">
import { nextTick, onBeforeUnmount, onMounted, ref, toRefs, useTemplateRef, watch } from 'vue'
import { useNotification } from '@kyvg/vue3-notification'
import { httpPost } from '@/lib/http'
import type { MarketFeaturedProductsResponse, MarketProductCardItem } from '@/types/market'
import ProductCard from '../products/ProductCard.vue'

const props = defineProps<{
  show?: boolean
  parentsOnly?: boolean
}>()

const { show, parentsOnly } = toRefs(props)
const { notify } = useNotification()
const slider = useTemplateRef<{
  swiper?: {
    slideNext: () => void
    slidePrev: () => void
    isEnd: boolean
    isBeginning: boolean
    on: (event: string, cb: () => void) => void
    off: (event: string, cb: () => void) => void
  }
}>('slider')

const items = ref<MarketProductCardItem[]>([])
const canSlidePrev = ref(false)
const canSlideNext = ref(false)

const sliderConfig = {
  spaceBetween: 16,
  breakpoints: {
    0: { slidesPerView: 1.25 },
    640: { slidesPerView: 2.25 },
    1345: { slidesPerView: 3.25 },
    1665: { slidesPerView: 4.25 },
    1985: { slidesPerView: 5.25 },
    2305: { slidesPerView: 6.25 },
    2625: { slidesPerView: 7.25 },
    2945: { slidesPerView: 8.25 },
    3265: { slidesPerView: 9.25 },
    3585: { slidesPerView: 10.25 }
  }
}

let fetchItemsTimeout: ReturnType<typeof setTimeout> | null = null

function fetchItems() {
  if (fetchItemsTimeout) clearTimeout(fetchItemsTimeout)

  fetchItemsTimeout = setTimeout(async () => {
    try {
      const response = await httpPost<MarketFeaturedProductsResponse>('/market/request_featured_products', {
        parents_only: !!parentsOnly.value,
        count: 1000
      })

      const responseItems = Array.isArray(response) ? response : response.data

      if (!Array.isArray(responseItems)) throw response
      items.value = responseItems
    } catch (err) {
      console.error(err)

      let title = 'Произошла неизвестная ошибка при получении рекомендуемых товаров. Повторите попытку позже.'
      if (
        (err as { failed?: boolean; message?: string })?.failed &&
        typeof (err as { message?: string }).message === 'string'
      ) {
        title = (err as { message: string }).message
      }

      notify({ type: 'error', title })
    }
  }, 300)
}

function updateNav() {
  const swiper = slider.value?.swiper
  if (!swiper) return

  canSlidePrev.value = !swiper.isBeginning
  canSlideNext.value = !swiper.isEnd
}

function slidePrev() {
  const swiper = slider.value?.swiper
  if (!swiper) return

  swiper.slidePrev()
  updateNav()
}

function slideNext() {
  const swiper = slider.value?.swiper
  if (!swiper) return

  swiper.slideNext()
  updateNav()
}

watch(show, () => {
  updateNav()
})
watch(items, () => {
  updateNav()
})
watch(parentsOnly, () => {
  fetchItems()
})

onMounted(() => {
  fetchItems()

  nextTick(() => {
    updateNav()
    const swiper = slider.value?.swiper
    if (!swiper) return

    swiper.on('slideChange', updateNav)
    swiper.on('reachBeginning', updateNav)
    swiper.on('reachEnd', updateNav)
  })
})

onBeforeUnmount(() => {
  const swiper = slider.value?.swiper
  if (swiper) {
    swiper.off('slideChange', updateNav)
    swiper.off('reachBeginning', updateNav)
    swiper.off('reachEnd', updateNav)
  }

  if (fetchItemsTimeout) clearTimeout(fetchItemsTimeout)
})
</script>

<template>
  <transition name="slide-fade-y">
    <section v-show="show && items.length" class="market-featured-products">
      <header class="market-featured-products__header">
        <h2 class="market-featured-products__title">МирГоворит рекомендует</h2>

        <v-link :disabled="!canSlidePrev" @click="slidePrev">
          <v-icon size="32">mdi-chevron-left</v-icon>
        </v-link>
        <v-link :disabled="!canSlideNext" @click="slideNext">
          <v-icon size="32">mdi-chevron-right</v-icon>
        </v-link>
      </header>

      <v-slider ref="slider" :config="sliderConfig">
        <ProductCard v-for="item in items" :key="item.id" :product="item" class="swiper-slide" />
      </v-slider>
    </section>
  </transition>
</template>

<style lang="scss">
.market-featured-products {
  padding: 24px;
  box-shadow: 0px 4px 8px 0px #00000033;
  border-radius: 16px;

  overflow: hidden;
  position: relative;
  padding-left: 32px;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    bottom: 0;
    width: 16px;
    background: linear-gradient(180deg, #ffcb75 0%, #ff7684 46.39%, #789afe 100%);
    z-index: -1;
  }

  @include mobile {
    padding: 16px;
    padding-left: 24px;

    &::before {
      width: 8px;
    }
  }

  &__header {
    display: flex;
    align-items: center;
    margin-bottom: 16px;
  }

  &__title {
    flex: 1;
    @include text-title;
    font-weight: bold;

    @include mobile {
      @include text-body;
    }
  }
}
</style>
